import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { getAnonymousID, getSessionID } from "redline-client-sdk";

import { useUserStore } from "@libs/acp/store/user/useUserStore";
import {
	clearACLocalData,
	handleRefreshToken,
	shouldRefreshToken,
} from "@libs/acp/utils/handleRefreshToken";

import { isAcOnCpHost } from "@libs/utils/helpers/isAcOnCpHost";

import { getAppName } from "@libs/utils/helpers/getAppName";
import { getIdentityBaseURL } from "./config";

const isCustomBuild = isAcOnCpHost();
const isDev = import.meta.env.VITE_ENV === "dev";

const privateIdentityApiClient = axios.create({
	headers: {
		"Content-Type": "application/json",
		"x-anonymous-id": getAnonymousID(),
		"x-session-id": getSessionID(),
	},
	validateStatus: (status) => {
		return status >= 200 && status < 300;
	},
});

const privateRequestsWithRefreshToken = [
	"/v2/public/auth/refresh-token",
	"/v2/app/delete",
];

privateIdentityApiClient.interceptors.request.use(
	async (config) => {
		config.headers["X-App-Id"] = getAppName();

		if (!config.baseURL) {
			config.baseURL = getIdentityBaseURL();
		}
		const shouldRefreshTokenBefore = await shouldRefreshToken();
		if (
			shouldRefreshTokenBefore &&
			config.url !== "/v2/public/auth/refresh-token"
		) {
			try {
				const response = await handleRefreshToken({
					refreshToken:
						useUserStore.getState().authenticatedUser.acp.auth.refresh_token ||
						"",
				});

				if (response?.access_token) {
					useUserStore.getState().updateACPAuthSession({
						access_token: response.access_token,
						refresh_token: response.refresh_token,
						isAuthenticated: true,
					});
				}
			} catch (err) {
				clearACLocalData();
				window.location.href = "/login";
				return Promise.reject(err);
			}
		}

		if (privateRequestsWithRefreshToken.includes(config.url || "")) {
			if (isCustomBuild) {
				config.baseURL = isDev
					? "https://dev-identity.consumidorpositivo.com.br"
					: "https://identity.consumidorpositivo.com.br";
			}

			config.headers["Refresh-Token"] =
				useUserStore.getState().authenticatedUser.acp.auth.refresh_token;
		}

		config.headers["Authorization"] =
			`Bearer ${useUserStore.getState().authenticatedUser.acp.auth.access_token}`;

		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

const forceLogout = (trigger: string) => {
	window.dispatchEvent(
		new CustomEvent("logout_trail", {
			detail: {
				trigger,
				source: "acp_private_identity_client",
				pathname: window.location.pathname,
			},
		}),
	);

	clearACLocalData();
	window.location.href = "/login";
};

privateIdentityApiClient.interceptors.response.use(
	(response) => response,
	async (error: AxiosError<{ error_slug?: string }>) => {
		const originalRequest = error.config as
			| (InternalAxiosRequestConfig & { _retry?: boolean })
			| undefined;

		const isRefreshRoute =
			originalRequest?.url === "/v2/public/auth/refresh-token";

		if (error.response?.data?.error_slug === "customer_not_found") {
			forceLogout("identity_customer_not_found_interceptor");
			return Promise.reject(error);
		}

		const isUnauthorized = error.response?.status === 401;

		if (!isUnauthorized || isRefreshRoute || !originalRequest) {
			return Promise.reject(error);
		}

		if (originalRequest._retry) {
			forceLogout("identity_401_after_refresh_retry");
			return Promise.reject(error);
		}

		originalRequest._retry = true;

		try {
			await handleRefreshToken({
				refreshToken:
					useUserStore.getState().authenticatedUser.acp.auth.refresh_token ||
					"",
			});
		} catch (refreshError) {
			forceLogout("identity_401_refresh_failed");
			return Promise.reject(refreshError);
		}

		return privateIdentityApiClient(originalRequest);
	},
);

export default privateIdentityApiClient;
